import React from "react";
import toast from "react-hot-toast";

export const REGISTRATION_TOAST_DURATION_MS = 10_000;

const base = {
  position: "top-right",
  className: "bul-reg-toast bul-reg-toast--loading",
  style: { maxWidth: "min(22rem, calc(100vw - 1.25rem))" },
};

function clipMsg(s, max = 96) {
  const t = String(s ?? "").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

/** One-line reason under "… registration failed" */
function clipReason(s, max = 120) {
  const t = String(s ?? "")
    .trim()
    .replace(/\s+/g, " ");
  if (!t) return "";
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

const REG_LABEL = {
  bulandi: "Bulandi registration",
  event: "Event sign-in",
  workshop: "Workshop registration",
  omerta: "Omertà registration",
  rifleShooting: "10.9 Rifle Shooting registration",
};

function registrationFlowLabel(kind) {
  return REG_LABEL[kind] || "Registration";
}

/** Flows that email a confirmation say so; the rest get their own line. */
const REG_SUCCESS_BODY = {
  omerta: "Your entry is recorded. Join the WhatsApp group for all updates.",
  rifleShooting: "Your entry is recorded. Join the WhatsApp group for all updates.",
};

function registrationSuccessBody(kind) {
  return REG_SUCCESS_BODY[kind] || "Please check your email for confirmation details.";
}

const richToastOpts = (variant, hasGroupLink) => ({
  position: "top-right",
  duration: REGISTRATION_TOAST_DURATION_MS,
  className: `bul-reg-toast bul-reg-toast--${variant} bul-reg-toast--rich`,
  style: {
    maxWidth: hasGroupLink
      ? "min(24rem, calc(100vw - 1.25rem))"
      : "min(22rem, calc(100vw - 1.25rem))",
  },
});

function registrationSuccessWhatsappLink(groupUrl) {
  const url = String(groupUrl ?? "").trim();
  if (!url) return null;
  return React.createElement(
    "p",
    { className: "bul-reg-toast-result__group" },
    React.createElement(
      "a",
      {
        href: url,
        target: "_blank",
        rel: "noopener noreferrer",
        className: "bul-reg-toast-result__group-link",
      },
      "Join the WhatsApp group",
    ),
  );
}

/**
 * In-flight registration: clear headline + reassurance (dismissed when request finishes).
 * @param {string} actionTitle
 * @param {string} [body] overrides the default "check your email" reassurance
 */
export function toastActionReceived(actionTitle, body) {
  const title = String(actionTitle || "Registration").trim() || "Registration";
  const content = React.createElement(
    "div",
    { className: "bul-reg-toast-loading" },
    React.createElement(
      "div",
      { className: "bul-reg-toast-loading__title" },
      `${title} — in progress`,
    ),
    React.createElement(
      "div",
      { className: "bul-reg-toast-loading__body" },
      String(body || "").trim() ||
        "Please check your email once registration completes.",
    ),
  );
  return toast.loading(content, { ...base, duration: Infinity });
}

export function toastActionSuccess(message) {
  return toast.success(clipMsg(message || "Done."), {
    position: "top-right",
    duration: REGISTRATION_TOAST_DURATION_MS,
    className: "bul-reg-toast bul-reg-toast--success",
    style: { maxWidth: "min(17rem, calc(100vw - 1.25rem))" },
  });
}

export function toastActionError(message) {
  return toast.error(clipMsg(message || "Something went wrong."), {
    position: "top-right",
    duration: REGISTRATION_TOAST_DURATION_MS,
    className: "bul-reg-toast bul-reg-toast--error",
    style: { maxWidth: "min(17rem, calc(100vw - 1.25rem))" },
  });
}

/** @param {'bulandi'|'event'|'workshop'|'omerta'|'rifleShooting'} kind @param {{ whatsappGroupUrl?: string, backgroundSyncNote?: string }} [opts] */
export function toastRegistrationSuccess(kind, opts = {}) {
  const label = registrationFlowLabel(kind);
  const groupUrl = String(opts.whatsappGroupUrl ?? "").trim();
  const backgroundNote = String(opts.backgroundSyncNote ?? "").trim();
  const content = React.createElement(
    "div",
    { className: "bul-reg-toast-result" },
    React.createElement(
      "div",
      { className: "bul-reg-toast-result__title" },
      `${label} successful`,
    ),
    React.createElement(
      "p",
      { className: "bul-reg-toast-result__body" },
      registrationSuccessBody(kind),
    ),
    backgroundNote
      ? React.createElement(
          "p",
          { className: "bul-reg-toast-result__body bul-reg-toast-result__body--muted" },
          backgroundNote,
        )
      : null,
    registrationSuccessWhatsappLink(groupUrl),
  );
  return toast.success(content, richToastOpts("success", Boolean(groupUrl)));
}

/** @param {'bulandi'|'event'|'workshop'|'omerta'|'rifleShooting'} kind */
export function toastRegistrationError(kind, reason) {
  const label = registrationFlowLabel(kind);
  const r = clipReason(reason);
  const reasonText = r || "Something went wrong. Please try again.";
  const content = React.createElement(
    "div",
    { className: "bul-reg-toast-result" },
    React.createElement(
      "div",
      { className: "bul-reg-toast-result__title" },
      `${label} failed`,
    ),
    React.createElement(
      "div",
      { className: "bul-reg-toast-result__reason" },
      reasonText,
    ),
  );
  return toast.error(content, richToastOpts("error", false));
}

export function dismissRegistrationToast(id) {
  if (id != null && id !== "") toast.dismiss(id);
}
